import { fabric } from "fabric";
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { fetchCanvasJson } from "shared/components/contextAPI/shared/RenderTemplate";
import { videoCompositionEnabled } from "shared/constants/assetExporter";
import {
  FeedColumn,
  FeedTblRow,
  TAssetBatch,
  TComposition,
  TConditionType,
  TTemplateComposition,
} from "shared/types/assetExporter";
import { ITemplate } from "shared/types/designStudio";
import {
  TBackgroundMedia,
  TCanvasJSON,
  TValueMapping,
  TValueMappings,
  TVariable,
} from "../types";
import { isImage } from "../validators";
import { useAssetBatchesContext } from "./AssetBatchesContext";
import {
  generateValueMappings,
  getBackgroundMediaForUrl,
  getMediaError,
  getNewCompositionInstance,
  getTemplatesPromises,
  getVarsFromCanvas,
  isTemplateComposition,
  parseValueMappings,
  replaceComposition,
  replaceCompositionVariable,
} from "./AssetBatchesContext.utils";

export type TRuleCondition = {
  index: number;
  operator: "and" | "or";
  columnName: string;
  comparisonOperator: TConditionType;
  value: string;
};

export const getEmptyRuleCondition = (index: number) => {
  return {
    index,
    operator: "and",
    columnName: "",
    comparisonOperator: "Equals",
    value: "",
  } as TRuleCondition;
};

interface ContextProps {
  emptyValueHighlight?: boolean;
  saveTemplate: (
    newTemplate: ITemplate,
    compositionCount: number,
    editingAssetBatch?: TAssetBatch,
    compositionToEdit?: TTemplateComposition,
  ) => Promise<void>;
  onSelectedRowChange: (row: FeedTblRow, compositionId?: string) => void;
  highlightVariable: (
    status: "enter" | "leave" | "focus" | "blur" | "emptyValue" | "fillValue",
    mappingKey: string,
  ) => void;
  onBackgroundMediaChange: (
    compositionId: string,
    media: TBackgroundMedia | undefined,
  ) => void;
  onBackgroundColumnChange: (compositionId: string, column?: string) => void;
  updateValueMappings: (
    variables: TVariable[],
    compositionToEdit: TTemplateComposition,
  ) => void;
  getMediaInputError: (
    value?: string,
    valueMapping?: TValueMapping,
  ) => string | undefined;
  useTemplateEffect: (
    editingAssetBatch: TAssetBatch | undefined,
    updateTemplates: (templates: ITemplate[]) => void,
  ) => void;
  selectedRow: any;
  hoveredMappingKey: string | undefined;
  setSelectedRow: React.Dispatch<any>;
  setHoveredMappingKey: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  changedMappingKey: string | undefined;
  setChangedMappingKey: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  updateCompositionVariables: (
    newVariables: TValueMappings,
    compositionId: string,
  ) => void;
  showVariablesOn: boolean;
  setShowVariablesOn: React.Dispatch<React.SetStateAction<boolean>>;
}

type ContextProviderProps = {
  children: ReactNode;
};

const Context = createContext<ContextProps>({} as ContextProps);

const ContextProvider = ({ children }: ContextProviderProps) => {
  const {
    setVariables,
    setCompositions,
    updateMaxStep,
    setTemplates,
    setHasFixedBackground,
    setBackgroundMedias,
    setBackgroundColumns,
    setCurrentCompositionId,
    setPreventAutoScroll,
    setCompositionToReplace,
    removeBackgrounds,
    setSelectedTemplate,
    currentStep,
    previewCount,
    mediaColumns,
    backgroundColumns,
    backgroundMedias,
    rows,
    feedId,
    compositionToReplace,
  } = useAssetBatchesContext();

  const [selectedRow, setSelectedRow] = useState<any>(rows[0]); //is the row of the table that is selected
  const [hoveredMappingKey, setHoveredMappingKey] = useState<string>();
  const [changedMappingKey, setChangedMappingKey] = useState<string>();
  const [emptyValueHighlight, setEmptyValueHighlight] = useState<boolean>();
  const [showVariablesOn, setShowVariablesOn] = useState(true);

  const updateCompositionVariables = useCallback(
    (newVariables: TValueMappings, compositionId: string) => {
      setCompositions(prevCompositions =>
        prevCompositions.map(comp =>
          replaceCompositionVariable(comp, newVariables, compositionId),
        ),
      );
    },
    [setCompositions],
  );

  const updateValueMappings = useCallback(
    (variables: TVariable[], compositionToEdit: TTemplateComposition) => {
      setVariables(oldVariables =>
        oldVariables.map(oldVar => {
          if (oldVar.compositionId === compositionToEdit.compositionId) {
            return {
              compositionId: compositionToEdit.compositionId,
              variables,
            };
          }
          return oldVar;
        }),
      );

      const mappings = generateValueMappings(variables, selectedRow);
      Object.keys(mappings).forEach(key => {
        if (compositionToEdit?.variables[key])
          mappings[key] = compositionToEdit.variables[key];
      });
      updateCompositionVariables(mappings, compositionToEdit.compositionId);
    },
    [selectedRow, updateCompositionVariables, setVariables],
  );

  const upsertComposition = useCallback(
    (composition: TComposition, isUpdate: boolean) => {
      if (!isUpdate)
        return setCompositions(oldCompositions => [
          ...oldCompositions,
          composition,
        ]);
      setCompositions(oldCompositions =>
        replaceComposition(oldCompositions, composition),
      );
    },
    [setCompositions],
  );

  const addOrReplaceTemplates = useCallback(
    (newTemplate: ITemplate, editingComposition?: TComposition) => {
      if (!editingComposition || !isTemplateComposition(editingComposition))
        return;
      if (!videoCompositionEnabled) return setTemplates([newTemplate]);

      setTemplates(oldTemplates => {
        const templatesFiltered = oldTemplates.filter(
          templ => templ.id !== editingComposition.template,
        );
        return [...templatesFiltered, newTemplate];
      });
    },
    [setTemplates],
  );

  const saveTemplate = useCallback(
    async (
      newTemplate: ITemplate,
      compositionCount: number,
      editingAssetBatch?: TAssetBatch,
      compositionToEdit?: TTemplateComposition,
    ) => {
      if (!newTemplate.id) return;
      updateMaxStep(currentStep);

      const currentComposition = compositionToEdit ?? compositionToReplace;

      const { canvasJsonUrl } = newTemplate;
      const json = canvasJsonUrl
        ? ((await fetchCanvasJson(canvasJsonUrl)) as TCanvasJSON)
        : undefined;

      const objNames = json?.objects.map(obj => obj.name ?? "");

      const filteredVariables: { [key: string]: TValueMapping } = Object.keys(
        currentComposition?.variables ?? {},
      ).reduce((acc: { [key: string]: TValueMapping }, key: string) => {
        if (
          objNames?.some(objName => key.includes(objName)) &&
          currentComposition?.variables[key]
        )
          acc[key] = currentComposition.variables[key];
        return acc;
      }, {});

      const composition = getNewCompositionInstance(
        feedId,
        newTemplate,
        compositionCount,
        currentComposition,
        editingAssetBatch,
        filteredVariables,
      );

      addOrReplaceTemplates(newTemplate, composition);
      if (videoCompositionEnabled) {
        setSelectedTemplate(undefined);
        setVariables(oldVariables => [
          ...oldVariables.filter(
            oldVariables =>
              oldVariables.compositionId !== currentComposition?.compositionId,
          ),
          {
            compositionId: composition.compositionId,
            variables: parseValueMappings(filteredVariables),
          },
        ]);

        upsertComposition(composition, !!currentComposition);

        setPreventAutoScroll(false);
        setCurrentCompositionId(composition.compositionId);
        if (currentComposition) {
          if (isTemplateComposition(currentComposition))
            removeBackgrounds(currentComposition);
          if (compositionToReplace) setCompositionToReplace(undefined);
        }
      } else {
        setVariables([
          { compositionId: composition.compositionId, variables: [] },
        ]);
        setCompositions([composition]);
      }

      /*  NOTE: Below process is required for variable highlighting and few things related to preview.
       *    For example, for a textbox, text and text lines arent exactly right unless we render with certain dimensions.
       *    So the rendering is necessary here in order to setup properly for variable highlight.
       */
      const canvas = new fabric.Canvas(document.createElement("canvas"));
      canvas.setDimensions({
        width: newTemplate.artboard.width,
        height: newTemplate.artboard.height,
      });

      canvas.loadFromJSON(json, () => {
        const backgroundImg = canvas.backgroundImage;
        if (isImage(backgroundImg) && backgroundImg.visible)
          setHasFixedBackground(true);

        const vars = getVarsFromCanvas(canvas);
        updateValueMappings(vars, composition);
      });
    },
    [
      updateMaxStep,
      currentStep,
      compositionToReplace,
      feedId,
      addOrReplaceTemplates,
      setSelectedTemplate,
      setVariables,
      upsertComposition,
      setPreventAutoScroll,
      setCurrentCompositionId,
      removeBackgrounds,
      setCompositionToReplace,
      setCompositions,
      setHasFixedBackground,
      updateValueMappings,
    ],
  );

  const onBackgroundMediaChange = useCallback(
    (compositionId: string, media: TBackgroundMedia | undefined) => {
      setBackgroundMedias(prevMedias => ({
        ...prevMedias,
        [compositionId]: media,
      }));
      setBackgroundColumns(prevColumns => ({
        ...prevColumns,
        [compositionId]: media?.column,
      }));
    },
    [setBackgroundColumns, setBackgroundMedias],
  );

  const onBackgroundColumnChange = useCallback(
    async (compositionId: string, column?: string) => {
      setBackgroundColumns(preColumns => ({
        ...preColumns,
        [compositionId]: column,
      }));
      if (!column) return;
      const media = await getBackgroundMediaForUrl(selectedRow[column], column);
      setBackgroundMedias(prevMedias => ({
        ...prevMedias,
        [compositionId]: media,
      }));
    },
    [setBackgroundColumns, selectedRow, setBackgroundMedias],
  );

  const onSelectedRowChange = async (
    row: FeedTblRow,
    compositionId?: string,
  ) => {
    setSelectedRow(row);
    if (!compositionId) return;
    if (
      backgroundMedias?.[compositionId]?.src &&
      !backgroundMedias?.[compositionId]?.column
    )
      return;
    if (!backgroundColumns?.[compositionId]) {
      setBackgroundMedias(prevMedias => ({
        ...prevMedias,
        [compositionId]: undefined,
      }));
      return;
    }
    // Update background media as selected row changes
    const media = await getBackgroundMediaForUrl(
      row[backgroundColumns[compositionId] as FeedColumn],
      backgroundColumns[compositionId],
    );
    setBackgroundMedias(prevMedias => ({
      ...prevMedias,
      [compositionId]: media,
    }));
  };

  const highlightVariable = (
    status: "enter" | "leave" | "focus" | "blur" | "emptyValue" | "fillValue",
    mappingKey: string,
  ) => {
    if (status === "fillValue") setEmptyValueHighlight(true);
    else setEmptyValueHighlight(false);

    setHoveredMappingKey(
      status === "leave" || status === "blur" ? undefined : mappingKey,
    );
  };

  const getMediaInputError = useCallback(
    (value?: string, valueMapping?: TValueMapping) =>
      getMediaError(previewCount, value, valueMapping, mediaColumns),
    [mediaColumns, previewCount],
  );

  const useTemplateEffect = (
    editingAssetBatch: TAssetBatch | undefined,
    updateTemplates: (templates: ITemplate[]) => void,
  ) => {
    useEffect(() => {
      if (editingAssetBatch) {
        const promises = getTemplatesPromises(editingAssetBatch);

        Promise.all(promises).then(results => {
          const templates = results
            .map(result => result?.result?.template)
            .filter(Boolean);
          updateTemplates(templates as ITemplate[]);
        });
      }
    }, [editingAssetBatch, updateTemplates]);
  };

  return (
    <Context.Provider
      value={{
        selectedRow,
        hoveredMappingKey,
        changedMappingKey,
        emptyValueHighlight,
        showVariablesOn,
        saveTemplate,
        onSelectedRowChange,
        highlightVariable,
        onBackgroundMediaChange,
        onBackgroundColumnChange,
        updateValueMappings,
        getMediaInputError,
        useTemplateEffect,
        setSelectedRow,
        setHoveredMappingKey,
        setChangedMappingKey,
        updateCompositionVariables,
        setShowVariablesOn,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const AssetBatchesValueMappingProvider = memo(ContextProvider);

export const useAssetBatchesValueMappingContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error("Context must be used within a ContextProvider");
  }

  return context;
};
